import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Mail } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { teamContent, memberPortraits } from '../data/team';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ContactSection from '../components/ContactSection';

const TeamPage = () => {
  const { language } = useLanguage();
  const t = teamContent[language] || teamContent.fr;

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
    const previousTitle = document.title;
    document.title = `${t.title} – Business Club France–Latvia`;
    return () => {
      document.title = previousTitle;
    };
  }, [t.title]);

  return (
    <div className="min-h-screen bg-[#1a1c1b] text-white" data-testid="team-page">
      <Header />

      <main className="pt-32 md:pt-36 pb-0">
        {/* Page hero */}
        <section className="max-w-[87.5rem] mx-auto px-6 md:px-10 mb-16 md:mb-20">
          <Link
            to="/"
            data-testid="team-back-link"
            className="group inline-flex items-center gap-2 text-[#888680] hover:text-[#d9fb06] transition-colors text-sm uppercase tracking-wider mb-10"
          >
            <ArrowLeft size={16} className="group-hover:-translate-x-1 transition-transform" />
            {t.ctaBack}
          </Link>

          <div className="max-w-3xl">
            <div className="flex items-center gap-3 mb-5">
              <div className="h-px w-10 bg-[#d9fb06]" />
              <span className="text-[#d9fb06] text-xs tracking-[0.25em] uppercase font-semibold">
                {t.eyebrow}
              </span>
            </div>
            <h1 className="font-black text-white text-4xl sm:text-5xl md:text-6xl lg:text-7xl uppercase leading-[0.92] mb-6">
              {t.title}
            </h1>
            <p className="text-[#888680] text-base md:text-lg lg:text-xl leading-relaxed">
              {t.subtitle}
            </p>
          </div>
        </section>

        {/* Members grid */}
        <section className="max-w-[87.5rem] mx-auto px-6 md:px-10 mb-20 md:mb-28">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            {t.members.map((member) => (
              <article
                key={member.id}
                data-testid={`team-member-${member.id}`}
                className="group relative"
              >
                {/* Portrait */}
                <div className="relative mb-5">
                  <div className="absolute -inset-1 border border-[#d9fb06]/20 rounded-sm transition-all duration-300 group-hover:border-[#d9fb06]/60" />
                  <div className="relative overflow-hidden rounded-sm aspect-[4/5] bg-[#0f1110]">
                    <img
                      src={memberPortraits[member.id].src}
                      alt={member.name}
                      style={{ objectPosition: memberPortraits[member.id].objectPosition }}
                      className="w-full h-full object-cover grayscale-[15%] group-hover:grayscale-0 group-hover:scale-[1.03] transition-all duration-500"
                    />
                  </div>
                </div>

                {/* Info */}
                <div>
                  <div className="text-[#d9fb06] text-[0.65rem] tracking-[0.2em] uppercase font-semibold mb-2">
                    {member.role}
                  </div>
                  <h3 className="text-white text-lg md:text-xl font-bold leading-tight mb-3">
                    {member.name}
                  </h3>
                  <a
                    href={`mailto:${member.email}`}
                    data-testid={`team-member-email-${member.id}`}
                    className="inline-flex items-center gap-2 text-[#888680] text-sm hover:text-[#d9fb06] transition-colors"
                  >
                    <Mail size={14} className="text-[#d9fb06]" />
                    <span className="break-all">{member.email}</span>
                  </a>
                </div>
              </article>
            ))}
          </div>
        </section>

        {/* Board candidacy block + form */}
        <section
          id="board-candidacy"
          data-testid="board-candidacy-section"
          className="border-t border-[#3f4816]/40 bg-[#161817]"
        >
          <div className="max-w-[87.5rem] mx-auto px-6 md:px-10 pt-16 md:pt-20 pb-2 text-center">
            <div className="inline-flex items-center gap-3 mb-5">
              <div className="h-px w-10 bg-[#d9fb06]" />
              <span className="text-[#d9fb06] text-xs tracking-[0.25em] uppercase font-semibold">
                Board
              </span>
              <div className="h-px w-10 bg-[#d9fb06]" />
            </div>
            <h2 className="font-black text-white text-3xl sm:text-4xl md:text-5xl uppercase leading-[0.95] mb-6 max-w-3xl mx-auto">
              {t.boardTitle}
            </h2>
            <p className="text-[#888680] text-base md:text-lg leading-relaxed max-w-2xl mx-auto">
              {t.boardSubtitle}
            </p>
          </div>

          {/* Reuse the same contact form */}
          <ContactSection />
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default TeamPage;
